import React, { useRef } from "react";
import FolderZip from "@mui/icons-material/FolderZip";
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { usePagePrefs } from "@pepsico/pref-permissions";

import OrthoFolderDropzone from "./OrthoFolderDropzone";
import ReviewFlightDialogContents from "./ReviewFlightDialogContents";
import { useGetLocationByUuid } from "api/locations/locations";
import type { LocationResponse } from "api/model";
import { TaskStatus } from "api/model";
import { useOrthoWorkflows } from "context/OrthoWorkflow";

interface UploadFlightDialogContentsProps {
  onCancel: () => void;
}

const UploadFlightDialogContents: React.FC<UploadFlightDialogContentsProps> = ({
  onCancel,
}) => {
  const {
    upload: { activeSession: activeOrthoUploadSession, analyzeFolder },
  } = useOrthoWorkflows();
  const zipInputRef = useRef<HTMLInputElement>(null);
  const selectedFiles = activeOrthoUploadSession?.selectedFiles ?? null;
  const resumeTasks = activeOrthoUploadSession?.resumeTasks ?? undefined;
  const [prefs] = usePagePrefs();

  const locationUuid = (prefs.location as string | null | undefined) ?? "";

  const {
    data: location = null,
    isError,
    isPending,
  } = useGetLocationByUuid(locationUuid, {
    query: {
      enabled: locationUuid !== "",
      select: (res): LocationResponse | null =>
        res.status === 200 ? res.data : null,
    },
  });

  if (locationUuid === "" || isPending || isError || location === null) {
    const message =
      locationUuid === ""
        ? "No location selected."
        : isPending
          ? "Loading location…"
          : "Location unavailable.";

    return (
      <>
        <DialogTitle>Upload drone imagery</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} variant="contained" color="primary">
            Cancel
          </Button>
        </DialogActions>
      </>
    );
  }

  const locationName =
    location.location_display_name ??
    `${location.location_name} (${location.city}, ${location.state_province})`;

  if (selectedFiles !== null && selectedFiles.length > 0) {
    return (
      <ReviewFlightDialogContents
        onCancel={onCancel}
        selectedFiles={selectedFiles}
        location={location}
        resumeTasks={resumeTasks}
      />
    );
  }
  const isBusy =
    (activeOrthoUploadSession?.isAnalyzing ?? false) ||
    activeOrthoUploadSession?.status === "uploading" ||
    activeOrthoUploadSession?.status === "hashing";
  const completedCount = resumeTasks?.filter(
    (t) => t.status === TaskStatus.completed,
  ).length;
  const totalCount = resumeTasks?.length;

  return (
    <>
      <DialogTitle>
        {resumeTasks != null ? "Resume upload" : "Upload Drone imagery"} -{" "}
        {locationName}
      </DialogTitle>
      <DialogContent>
        {resumeTasks != null && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: "medium" }}>
              {completedCount} of {totalCount} images are already uploaded
            </Typography>
            <Typography variant="body2">
              Select the whole folder again — images already in the bucket are
              skipped.
            </Typography>
          </Alert>
        )}
        <OrthoFolderDropzone />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <input
          ref={zipInputRef}
          accept=".zip,application/zip,application/x-zip-compressed"
          hidden
          type="file"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            if (files.length > 0) {
              void analyzeFolder(files);
            }
            event.target.value = "";
          }}
        />
        <Button
          disabled={isBusy}
          startIcon={<FolderZip />}
          onClick={() => zipInputRef.current?.click()}
        >
          Choose a .zip
        </Button>
        <Button onClick={onCancel} variant="contained" color="error">
          Cancel
        </Button>
      </DialogActions>
    </>
  );
};

export default UploadFlightDialogContents;
